<template lang="pug" src="./news.pug"></template>
<style lang="scss" src="./news.scss" scoped></style>

<script>
import { mapGetters, mapState } from 'vuex'
import scroll from '@/utils/scroll'
import axios from 'axios'
import { markdown } from '@/utils/helpers'

export default {
    data: () => ({
        filters: {
            fromDate: null,
            toDate: null,
            header: null,
            showTo: null
        },
        removeDialog: false,
        imageBaseUrl: process.env.VUE_APP_BASE_API.replace(/v1\//, 'uploads/'),
        sharingUrl: null,
        referralCode: {
            text: null,
            value: null
        },
        moreInfoDialog: false,
        loadImageDialog: false,
        shareDialog: false,
        showCodes: false,
        imageShareCode: {
            code: null,
            link: null,
            size: 150,
            position: 'top-left'
        }
    }),
    computed: {
        ...mapGetters(['type']),
        ...mapState({
            referralCodes: state => state.codes.referralCodes,
            pagination: state => state.news.pagination,
            appliedFilters: state => state.news.filters,
            news: state => state.news.news
        }),
        isLoaded: {
            get() {
                return this.$store.state.news.isLoaded
            },
            set(val) {
                this.$store.commit('SET_LOAD', val)
            }
        },

        canShare: function() {
            return this.type !== 'admin' &&
                this.referralCodes &&
                this.referralCodes.length
        },

        shareApiAvailable() {
            return !!navigator.share
        },

        showToSelects: function() {
            const typeSelects = '$vuetify.pages.News.selects'
            return ['all', 'leader', 'business'].map(it => ({
                text: this.$vuetify.t(`${typeSelects}.${it}`),
                value: it === 'all' ? null : it
            }))
        },

        codePositions: function() {
            return ['top-left', 'bottom-left', 'top-right', 'bottom-right'].map(it => ({
                text: this.$vuetify.t(`$vuetify.pages.News.shareImageBlock.code.positions.${it}`),
                value: it
            }))
        },

        referralCodesSelects: function() {
            return this.referralCodes.map(it => ({
                text: `${it.name} - ${it.code}`,
                value: it.code
            }))
        },

        canEditNews: function() {
            return this.type === 'admin'
        },

        urls: function() {
            return {
                'business': `${process.env.VUE_APP_FRONT}/ref-register/`,
                'leader': `${process.env.VUE_APP_FRONT}/business-register/`
            }
        },

        disableDownload() {
            return this.showCodes && (
                !this.imageShareCode.code ||
                (this.imageShareCode.size < 1 || this.imageShareCode.size > 2000)
            )
        }
    },
    created: function() {
        this.filters = Object.assign({}, this.appliedFilters)
        this.getNewsByPage(this.pagination.page)
        this.$store.dispatch('codes/getReferralCodes', 1)
    },
    beforeRouteLeave: function(to, from, next) {
        this.$store.commit('news/SET_PAGINATION', { page: 1, length: null })
        this.$store.commit('news/RESET_NEWS')
        next()
    },
    methods: {
        markdown,
        instagramShare() {
            if (this.shareApiAvailable) {
                navigator.share({
                    title: 'SPRUT',
                    url: this.sharingUrl
                }).then(() => {
                    this.$store.dispatch('addMessage', {
                        message: 'linkShared',
                        type: 'success',
                        locale: true
                    })
                }).catch(() => {
                    // Catch iOS bug
                    window.location.reload(true)
                })
            }
        },
        openShareDialog: function(item) {
            this.referralCode = null
            this.shareDialog = true
            this.feed = item
        },

        setShareUrl() {
            this.sharingUrl = `${process.env.VUE_APP_FRONT}/news/${this.feed.uuid}/${this.referralCode}`
        },

        openDownloadDialog: function(item) {
            this.feed = item
            this.loadImageDialog = true
        },

        openMoreInfoDialog: function(feed) {
            this.moreInfoDialog = true
            this.feed = feed
        },

        closeMoreInfoDialog: function() {
            this.moreInfoDialog = false
        },

        openRemoveDialog: function(feed) {
            this.removeDialog = true
            this.feed = feed
        },

        deleteFeed: async function() {
            await this.$store.dispatch('news/deleteFeed', this.feed.id).then(() => {
                this.removeDialog = false
                this.getNewsByPage()
            })
        },

        getDataUri(url, qr, callback) {
            const img = new Image()
            img.crossOrigin = 'Anonymous'

            const position = this.imageShareCode.position
            const size = this.imageShareCode.size

            img.onload = function() {
                const canvas = document.createElement('CANVAS')
                const ctx = canvas.getContext('2d')

                canvas.height = this.height
                canvas.width = this.width

                let x = 0
                let y = 0

                switch (position) {
                    case 'bottom-left':
                        x = 0
                        y = canvas.height - size
                        break
                    case 'bottom-right':
                        x = canvas.width - size
                        y = canvas.height - size
                        break
                    case 'top-right':
                        x = canvas.width - size
                        y = 0
                        break
                    case 'top-left':
                    default:
                        break
                }

                ctx.drawImage(this, 0, 0)
                ctx.drawImage(qr, x, y, size, size)
                const dataURL = canvas.toDataURL('image/png')

                callback(dataURL)
            }

            img.src = url
        },

        changeQrCode(code) {
            this.imageShareCode.code = code
            this.imageShareCode.link = this.urls[this.type] + code
        },

        download: async function() {
            const imageUrl = `${this.imageBaseUrl}${this.feed.image && this.feed.image.filename}`
            const type = this.getTypeFromUrl(imageUrl)

            if (!type) return

            if (!this.showCodes) {
                axios({
                    method: 'get',
                    url: imageUrl,
                    responseType: 'arraybuffer'
                }).then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data]))

                    this.downloadBaseHandler(url, type)
                })
            } else {
                const qrCodeCanvas = await this.$refs.qrCode.$el

                this.getDataUri(imageUrl, qrCodeCanvas, (dataUri) => {
                    this.downloadBaseHandler(dataUri, {
                        extension: 'png'
                    })
                })
            }
        },

        downloadBaseHandler(url, type) {
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `image.${type.extension}`)
            document.body.appendChild(link)
            link.click()
            link.remove()
        },

        resetImageShareCode() {
            this.showCodes = false
            this.imageShareCode = {
                code: null,
                link: null,
                size: 150,
                position: 'top-left'
            }
        },

        resetFilters: function() {
            this.filters = Object.assign({}, {
                type: null,
                status: null,
                fromDate: null,
                toDate: null,
                header: null,
                showTo: null
            })
            this.$store.commit('news/RESET_NEWS')
            this.getNewsByPage()
        },

        getTypeFromUrl(url) {
            const type = url.match(/\.(jpeg|jpg|png)$/)
            const assoc = {
                'jpeg': {
                    type: 'image/jpeg',
                    extension: 'jpeg'
                },
                'jpg': {
                    type: 'image/jpg',
                    extension: 'jpg'
                },
                'png': {
                    type: 'image/png',
                    extension: 'png'
                }
            }

            if (!type) return

            return assoc[type[1]]
        },

        onPageChange: function(page) {
            if (page !== this.pagination.page) {
                this.getNewsByPage(page, true)
            }
        },

        changeFromDate: function(date) {
            this.filters.fromDate = date
        },

        changeToDate(date) {
            this.filters.toDate = date
        },

        submit: function() {
            this.$store.commit('news/SET_FILTERS', Object.assign({}, this.filters))
            this.getNewsByPage()
        },

        getNewsByPage: function(page = 1, isScrollEnable = false) {
            this.$store.dispatch('news/getNews', {
                page,
                ...this.appliedFilters
            }).then(_ => {
                if (isScrollEnable) {
                    scroll(this.$refs.news)
                }
            })
        },
        goToEditor(id) {
            const redirectUrl = id ? `news/editor/${id}` : 'news/editor'
            this.$router.push(redirectUrl)
        }
    }
}
</script>
